/**
 * /login route. Public, sits OUTSIDE the Layout's AuthGate.
 *
 * Three explicit, equal sign-in choices. Access is decided by the database
 * after sign-in, so any Microsoft or Google account can start the flow and
 * there is no routing-by-email-domain:
 *   - Sign in with Microsoft -> Microsoft Entra OIDC start (any tenant).
 *   - Sign in with Google    -> Google Workspace OIDC start (any domain).
 *   - Sign in with an email code -> the API emails a 6-digit code
 *     (POST /v1/core/auth/email/start), the user enters it (POST
 *     /v1/core/auth/email/verify), and on success the API sets the same
 *     session cookie the OIDC callbacks set.
 *
 * All three land the SPA the same way: a full navigation to returnTo, after
 * which AuthGate re-fetches /me with the session cookie.
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getEntraLoginUrl, getGoogleLoginUrl } from '@/lib/useSession';
import { apiPost, type ApiError } from '@/lib/api';
import { API_BASE } from '@/lib/apiBase';

function safeReturn(raw: string | null): string {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return '/';
  return raw;
}

// Basic client-side email shape check before hitting the email-code endpoints.
// The one-time-code path works for ANY valid email, so it checks format only.
function looksLikeEmail(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

// Pull a human-readable message out of an API error response, falling back to
// status-based copy for the states the flow calls out (expired, too many
// attempts) and finally to a generic message.
function apiErrorMessage(err: unknown, fallback: string): string {
  const e = err as ApiError;
  const body = e?.body;
  if (body && typeof body === 'object') {
    const rec = body as Record<string, unknown>;
    const msg = rec.error ?? rec.message;
    if (typeof msg === 'string' && msg.trim()) return msg;
  }
  if (typeof body === 'string' && body.trim()) return body;
  if (e?.status === 429) return 'Too many attempts. Please wait a bit and try again.';
  if (e?.status === 410) return 'That code has expired. Request a new one.';
  return fallback;
}

export function Login() {
  const [params] = useSearchParams();
  const returnTo = safeReturn(params.get('return'));

  // Email + one-time-code sign-in state. `emailMode` reveals the email/code
  // inputs when the user picks the third option.
  const [emailMode, setEmailMode] = useState(false);
  const [email, setEmail] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [codeNotice, setCodeNotice] = useState<string | null>(null);

  async function sendCode() {
    const trimmed = email.trim();
    setCodeError(null);
    setCodeNotice(null);
    if (!looksLikeEmail(trimmed)) {
      setCodeError('Enter a valid work email above, then request a code.');
      return;
    }
    setSending(true);
    try {
      await apiPost(`${API_BASE}/v1/core/auth/email/start`, { email: trimmed });
      setCodeSent(true);
      setCodeNotice(`We sent a 6-digit code to ${trimmed}. Enter it below.`);
    } catch (err) {
      setCodeError(apiErrorMessage(err, 'Could not send a code. Check the email and try again.'));
    } finally {
      setSending(false);
    }
  }

  async function verifyCode(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = email.trim();
    const c = code.trim();
    setCodeError(null);
    if (c.length === 0) {
      setCodeError('Enter the 6-digit code from your email.');
      return;
    }
    setVerifying(true);
    try {
      await apiPost(`${API_BASE}/v1/core/auth/email/verify`, { email: trimmed, code: c });
      // The API set the session cookie (same as the OIDC callbacks). A full
      // navigation to returnTo lands the SPA exactly as an OIDC login does, and
      // AuthGate re-fetches /me with the new cookie.
      window.location.assign(returnTo);
    } catch (err) {
      setCodeError(apiErrorMessage(err, 'That code is not valid. Check it and try again.'));
      setVerifying(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-amp-cream px-4 dark:bg-amp-dark-bg">
      <div className="w-full max-w-md rounded-lg border border-amp-line bg-amp-white p-8 shadow-sm dark:border-amp-dark-line dark:bg-amp-dark-surface">
        <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-2 text-center">
          Cockpit
        </p>
        <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink mb-6 text-center">
          Sign in
        </h1>

        <div className="flex flex-col gap-2">
          <a
            href={getEntraLoginUrl(returnTo)}
            className="inline-flex w-full items-center justify-center rounded-md border border-amp-line bg-amp-white px-4 py-2 text-sm font-medium text-amp-ink hover:border-amp-olive hover:bg-amp-cream dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:hover:border-amp-olive-dark dark:hover:bg-amp-dark-surface-warm"
          >
            Sign in with Microsoft
          </a>
          <a
            href={getGoogleLoginUrl(undefined, returnTo)}
            className="inline-flex w-full items-center justify-center rounded-md border border-amp-line bg-amp-white px-4 py-2 text-sm font-medium text-amp-ink hover:border-amp-olive hover:bg-amp-cream dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:hover:border-amp-olive-dark dark:hover:bg-amp-dark-surface-warm"
          >
            Sign in with Google
          </a>
          <button
            type="button"
            onClick={() => setEmailMode((v) => !v)}
            aria-expanded={emailMode}
            className="inline-flex w-full items-center justify-center rounded-md border border-amp-line bg-amp-white px-4 py-2 text-sm font-medium text-amp-ink hover:border-amp-olive hover:bg-amp-cream dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:hover:border-amp-olive-dark dark:hover:bg-amp-dark-surface-warm"
          >
            Sign in with an email code
          </button>
        </div>

        {emailMode ? (
          <div className="mt-4 rounded-md border border-amp-line p-4 dark:border-amp-dark-line">
            <form
              onSubmit={(ev) => {
                ev.preventDefault();
                void sendCode();
              }}
              className="space-y-3"
            >
              <label
                htmlFor="login-email"
                className="block text-sm font-medium text-amp-ink dark:text-amp-dark-ink"
              >
                Work email
              </label>
              <input
                id="login-email"
                type="email"
                autoFocus
                autoComplete="email"
                inputMode="email"
                placeholder="you@ampersandbrands.com"
                value={email}
                onChange={(ev) => {
                  setEmail(ev.target.value);
                  // Changing the email invalidates any code already sent.
                  if (codeSent || codeError || codeNotice) {
                    setCodeSent(false);
                    setCode('');
                    setCodeError(null);
                    setCodeNotice(null);
                  }
                }}
                className="w-full rounded-md border border-amp-line bg-amp-white px-3 py-2 text-sm text-amp-ink placeholder:text-amp-ink-muted focus:border-amp-olive focus:outline-none dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:placeholder:text-amp-dark-ink-muted"
              />
              <button
                type="submit"
                disabled={sending}
                className="inline-flex w-full items-center justify-center rounded-md bg-amp-olive px-4 py-2 text-sm font-medium text-amp-cream hover:bg-amp-olive-dark disabled:opacity-50 disabled:hover:bg-amp-olive dark:bg-amp-olive-dark dark:text-amp-dark-bg dark:hover:bg-amp-olive"
              >
                {sending ? 'Sending code...' : codeSent ? 'Resend code' : 'Email me a one-time code'}
              </button>
            </form>

            {codeError ? (
              <p className="mt-3 text-sm text-red-700 dark:text-red-300">{codeError}</p>
            ) : null}

            {codeSent ? (
              <form onSubmit={verifyCode} className="mt-3 space-y-3">
                {codeNotice ? (
                  <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">{codeNotice}</p>
                ) : null}
                <label
                  htmlFor="login-code"
                  className="block text-sm font-medium text-amp-ink dark:text-amp-dark-ink"
                >
                  One-time code
                </label>
                <input
                  id="login-code"
                  type="text"
                  autoFocus
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="123456"
                  value={code}
                  onChange={(ev) => {
                    setCode(ev.target.value.replace(/\D/g, ''));
                    if (codeError) setCodeError(null);
                  }}
                  className="w-full rounded-md border border-amp-line bg-amp-white px-3 py-2 text-sm tracking-widest text-amp-ink placeholder:text-amp-ink-muted focus:border-amp-olive focus:outline-none dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:placeholder:text-amp-dark-ink-muted"
                />
                <button
                  type="submit"
                  disabled={verifying || code.trim().length === 0}
                  className="inline-flex w-full items-center justify-center rounded-md bg-amp-olive px-4 py-2 text-sm font-medium text-amp-cream hover:bg-amp-olive-dark disabled:opacity-50 disabled:hover:bg-amp-olive dark:bg-amp-olive-dark dark:text-amp-dark-bg dark:hover:bg-amp-olive"
                >
                  {verifying ? 'Verifying...' : 'Verify'}
                </button>
              </form>
            ) : null}
          </div>
        ) : null}

        <p className="mt-5 text-center text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
          Sign in with your Microsoft or Google account, or request a one-time code by email. Access is set up per account.
        </p>
      </div>
    </div>
  );
}
